import loaderGif from "../../Assests/loader.gif";
import { VendorsColumns } from "../../ColumnsConfig/VendorsColumn";
import Table from "../../Components/Table";
import {
  fetchVendors,
  fetchCommodityCodes,
  copyVendorRow,
} from "../../Services/Vendors";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Select,
  MenuItem,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import React, { useState, useEffect, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Constants
const INITIAL_SEARCH_VALUES = {
  supplier_site_id: "",
  dept_number: "",
  vendor_name: "",
  commodity_code: "",
  fob_terms: "",
  chain: "",
  area: "",
  brand_id: "",
};

const INITIAL_TABLE_STATE = {
  rows: [],
  totalRecords: 0,
  currentPage: 1,
  pageSize: 50,
  sortParams: { column: "vendor_id", order: "asc" },
  range: { startRecord: 1, endRecord: 5 },
};

const FIELD_MAPPINGS = {
  supplier_site_id: {
    label: "Supplier Site ID",
    apiParam: "supplierSiteId",
    type: "number",
  },
  dept_code: {
    label: "Department Number",
    apiParam: "departmentNumber",
    type: "number",
  },
  vendor_name: { label: "Vendor Name", apiParam: "vendor_name", type: "text" },
  commodity_code: {
    label: "Commodity Code",
    apiParam: "commodityCode",
    type: "select",
  },
  fob_terms: { label: "FOB Terms", apiParam: "fobTerms", type: "select" },
  chain: { label: "Chain", apiParam: "chain", type: "text" },
  area: { label: "Area", apiParam: "area", type: "text" },
  brand_id: { label: "Brand ID", apiParam: "brandId", type: "text" },
  brand_desc: {
    label: "Brand Description",
    apiParam: "brandDesc",
    type: "text",
  },
};

const FOB_TERMS_OPTIONS = [
  { value: "Origin", label: "Origin" },
  { value: "Consolidator", label: "Consolidator" },
  { value: "Destination", label: "Destination" },
  { value: "Other", label: "Other" },
];

const Vendors = () => {
  const [searchValues, setSearchValues] = useState(INITIAL_SEARCH_VALUES);
  const [copyLoading, setCopyLoading] = useState(false);

  const [tableState, setTableState] = useState(INITIAL_TABLE_STATE);
  const [commodityCodes, setCommodityCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copyDialog, setCopyDialog] = useState({ open: false, data: null });
  const [activeSearchParams, setActiveSearchParams] = useState({});
  const nonEditableFields = ["vendor_name"];
  const numberValidationFields = ["chain", "area", "brand_id"];
  const [fieldErrors, setFieldErrors] = useState({});

  const loadVendors = useCallback(
    async (searchParams = {}) => {
      setLoading(true);
      try {
        const { currentPage, pageSize, sortParams } = tableState;
        const data = await fetchVendors(
          searchParams,
          currentPage,
          pageSize,
          sortParams.column,
          sortParams.order
        );

        if (data?.data) {
          const mappedRows = data.data.map((vendor) => ({
            ...vendor,
            id: vendor.vendor_id,
          }));

          const startRecord = (currentPage - 1) * pageSize + 1;
          const endRecord = Math.min(
            currentPage * pageSize,
            parseInt(data.totalRecords)
          );

          setTableState((prev) => ({
            ...prev,
            rows: mappedRows,
            totalRecords: parseInt(data.totalRecords) || 0,
            range: { startRecord, endRecord },
          }));
        } else {
          setTableState((prev) => ({
            ...prev,
            rows: [],
            totalRecords: 0,
            range: { startRecord: 0, endRecord: 0 },
          }));
        }
      } catch (error) {
        console.error("Error fetching vendors:", error);
        toast.error("Failed to fetch vendors data");
        setTableState((prev) => ({
          ...prev,
          rows: [],
          totalRecords: 0,
          range: { startRecord: 0, endRecord: 0 },
        }));
      } finally {
        setLoading(false);
      }
    },
    [tableState.currentPage, tableState.pageSize, tableState.sortParams]
  );

  useEffect(() => {
    loadVendors(activeSearchParams);
  }, [
    tableState.currentPage,
    tableState.pageSize,
    tableState.sortParams,
    activeSearchParams,
    loadVendors,
  ]);

  useEffect(() => {
    const initializeCommodityCodes = async () => {
      try {
        const data = await fetchCommodityCodes();
        setCommodityCodes(Array.isArray(data.data) ? data.data : []);
      } catch (error) {
        console.error("Error fetching commodity codes:", error);
        toast.error("Failed to fetch commodity codes");
        setCommodityCodes([]);
      }
    };
    initializeCommodityCodes();
  }, []);

  const handleSearch = () => {
    const searchParams = Object.entries(searchValues)
      .filter(([_, value]) => value)
      .reduce(
        (acc, [key, value]) => ({
          ...acc,
          [FIELD_MAPPINGS[key].apiParam]: value,
        }),
        {}
      );

    setActiveSearchParams(searchParams);
    setTableState((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleInputChange = (field, value) => {
    setSearchValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSort = useCallback((column, order) => {
    setTableState((prev) => ({
      ...prev,
      sortParams: { column, order },
    }));
  }, []);

  const handlePageChange = useCallback((page, newPageSize) => {
    setTableState((prev) => ({
      ...prev,
      currentPage: page,
      pageSize: newPageSize || prev.pageSize,
    }));
  }, []);

  const validateCopyData = (data) => {
    const numericFields = ["supplier_site_id", "dept_number", "brand_id"];
    for (const field of numericFields) {
      if (data[field] && isNaN(Number(data[field]))) {
        toast.error(
          `${FIELD_MAPPINGS[field]?.label || field} must be a number`
        );
        return false;
      }
    }
    return true;
  };

  const handleCopyVendor = async () => {
    if (!copyDialog.data) return;
    if (!validateCopyData(copyDialog.data)) return;

    setCopyLoading(true); // Start the loader

    try {
      const response = await copyVendorRow(copyDialog.data);
      if (response.data) {
        toast.success("Vendor created successfully");
        loadVendors(activeSearchParams);
        setCopyDialog({ open: false, data: null });
      } else {
        toast.error("Failed to create vendor");
      }
    } catch (error) {
      console.error("Error creating vendor:", error);
      toast.error("Failed to create vendor");
    } finally {
      setCopyLoading(false); // Stop the loader
    }
  };

  const renderSearchFields = () => (
    <Grid container spacing={2} alignItems="center">
      {Object.entries(FIELD_MAPPINGS)
        .filter(([field]) => field !== "brand_desc")
        .map(([field, { label, type }]) => (
          <Grid item xs={3} key={field}>
            <Box display="flex" alignItems="center">
              <Typography variant="body1" sx={{ mr: 1, width: "150px" }}>
                {label}
              </Typography>
              {field === "commodity_code" ? (
                <Select
                  value={searchValues[field]}
                  onChange={(e) => handleInputChange(field, e.target.value)}
                  displayEmpty
                  size="small"
                  sx={{ width: "200px" }}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {commodityCodes.map((commodity) => (
                    <MenuItem
                      key={commodity.commodity_code_id}
                      value={commodity.commodity_code_id}
                    >
                      {commodity.name}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <TextField
                  variant="outlined"
                  size="small"
                  value={searchValues[field]}
                  onChange={(e) => handleInputChange(field, e.target.value)}
                  type={type === "number" ? "number" : "text"}
                  sx={{ width: "200px" }}
                />
              )}
            </Box>
          </Grid>
        ))}
      <Grid item xs={12}>
        <Button
          variant="contained"
          sx={{ backgroundColor: "#3f506b", textTransform: "none", ml: 2 }}
          onClick={handleSearch}
        >
          Search
        </Button>
      </Grid>
    </Grid>
  );

  const renderCopyDialog = () => {
    const validateNumberField = (field, value) => {
      if (numberValidationFields.includes(field)) {
        const isValid = value === "" || !isNaN(Number(value));
        setFieldErrors((prev) => ({
          ...prev,
          [field]: !isValid
            ? `${FIELD_MAPPINGS[field].label} must be a number`
            : "",
        }));
        return isValid;
      }
      return true;
    };

    const handleFieldChange = (field, value) => {
      if (validateNumberField(field, value)) {
        setCopyDialog((prev) => ({
          ...prev,
          data: { ...prev.data, [field]: value },
        }));
      }
    };

    const hasErrors = Object.values(fieldErrors).some((error) => error !== "");

    return (
      <Dialog
        open={copyDialog.open}
        onClose={() => setCopyDialog({ open: false, data: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Copy Vendor</DialogTitle>
        <DialogContent>
          {copyLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="200px"
            >
              <img src={loaderGif} alt="Processing..." width={50} />
            </Box>
          ) : (
            copyDialog.data && (
              <Box sx={{ mt: 2 }}>
                {Object.values(fieldErrors)
                  .filter((error) => error !== "")
                  .map((error, index) => (
                    <Alert
                      key={`global-error-${index}`}
                      severity="error"
                      sx={{ mb: 2 }}
                    >
                      {error}
                    </Alert>
                  ))}

                {Object.entries(FIELD_MAPPINGS).map(
                  ([field, { label, type }]) => {
                    if (field === "commodity_code") {
                      return (
                        <TextField
                          key={field}
                          label={label}
                          value={copyDialog.data.commodity_code_id || ""}
                          onChange={(e) =>
                            setCopyDialog((prev) => ({
                              ...prev,
                              data: {
                                ...prev.data,
                                commodity_code_id: e.target.value,
                                commodity_code_name:
                                  commodityCodes.find(
                                    (code) =>
                                      code.commodity_code_id === e.target.value
                                  )?.name || "",
                              },
                            }))
                          }
                          select
                          fullWidth
                          margin="normal"
                        >
                          {commodityCodes.map((commodity) => (
                            <MenuItem
                              key={commodity.commodity_code_id}
                              value={commodity.commodity_code_id}
                            >
                              {commodity.commodity_code} - {commodity.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      );
                    }
                    if (field === "fob_terms") {
                      return (
                        <TextField
                          key={field}
                          label={label}
                          value={copyDialog.data[field] || ""}
                          onChange={(e) =>
                            setCopyDialog((prev) => ({
                              ...prev,
                              data: { ...prev.data, [field]: e.target.value },
                            }))
                          }
                          select
                          fullWidth
                          margin="normal"
                        >
                          {FOB_TERMS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      );
                    }
                    return (
                      <TextField
                        key={field}
                        label={label}
                        value={copyDialog.data[field] || ""}
                        onChange={(e) =>
                          handleFieldChange(field, e.target.value)
                        }
                        type={type === "number" ? "number" : "text"}
                        fullWidth
                        margin="normal"
                        disabled={nonEditableFields.includes(field)}
                        error={!!fieldErrors[field]}
                        helperText={fieldErrors[field]}
                      />
                    );
                  }
                )}
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCopyDialog({ open: false, data: null })}
            disabled={copyLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCopyVendor}
            variant="contained"
            color="primary"
            disabled={copyLoading || hasErrors}
          >
            {copyLoading ? "Processing..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <div className="px-5 my-1">
      <ToastContainer position="top-right" autoClose={3000} />

      <Box mb={2}>{renderSearchFields()}</Box>

      <Box position="relative" mt={2}>
        {loading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            justifyContent="center"
            alignItems="center"
            bgcolor="rgba(255, 255, 255, 0.8)"
            zIndex={1}
          >
            <img src={loaderGif} alt="Loading..." width={50} />
          </Box>
        )}

        {!loading && tableState.rows.length === 0 ? (
          <Alert severity="info">No records found.</Alert>
        ) : (
          <Table
            rows={tableState.rows}
            columns={VendorsColumns((rowData) =>
              setCopyDialog({ open: true, data: rowData })
            )}
            paginationMode="server"
            currentPage={tableState.currentPage}
            pageSize={tableState.pageSize}
            totalRecords={tableState.totalRecords}
            onPageChange={handlePageChange}
            onSort={handleSort}
            startRecord={tableState.range.startRecord}
            endRecord={tableState.range.endRecord}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        )}
      </Box>

      {renderCopyDialog()}
    </div>
  );
};

export default Vendors;
